/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import Dates from "utils/Dates";
import {itemSlots} from "utils/bookingUtils";
import {
    BookingRecapitulationAccessSlotItem,
    BookingRecapitulationSlotItem,
} from "pages/Front/Booking/components/BookingCustomer/BookingRecapitulationSlotItem";
import {PackageCategory} from "types/enums/PackageCategory";
import {DEFAULT_HALL, SHOW_IMPORTANT_ARRIVE_TIME, SHOW_ITEM_SLOT_RANGE_ONLY} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import ImportantArriveTime from "pages/Front/Booking/components/ImportantArriveTime";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useTranslation} from "react-i18next";
import {PackagesCart} from "types/entities/PackagesCart";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {filterAccessItemSlots} from "utils/packageUtils";
import clsx from "clsx";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {ClockIcon, RectangleStackIcon, UsersIcon} from "@heroicons/react/24/outline";

const SoldTimeslots = (props: {
    packagesCart: PackagesCart;
    packagesCategory: PackageCategory;
    packagesCartItemsOutstanding: PackagesCartItem[] | null | undefined;
    className?: string;
    centered?: boolean;
    onClick: () => void;
}) => {
    const {t} = useTranslation();

    if (!props.packagesCart.packagesCartItemSlots) {
        return (
            <div className={"BookingRecapitulation"}>
                <CardComponent>{t("No timeslots chosen")}</CardComponent>
            </div>
        );
    }

    const packageCartItemSlots = itemSlots(props.packagesCart.packagesCartItemSlots, props.packagesCategory);
    return (
        <BookingCard className={clsx("ReservationsForDate !px-0 !py-0", props.className)}>
            <h2 className={clsx("BookingCardTitle font-bold text-xl text-black px-3 py-2.5 xl:px-3 xl:py-3 flex items-center justify-between")}>
                <div className={"inline-flex items-center gap-x-2"}>
                    <span>{t("Your time plan")}:</span>
                    <span>{Dates.date(props.packagesCart.date)}</span>
                </div>

                <button className={"bg-blue-400 hover:bg-blue-500 px-2 py-1 text-white rounded text-base"} onClick={props.onClick}>
                    {t("Rebook")}
                </button>
            </h2>

            <div className={"flex flex-col"}>
                <div className={"grid grid-cols-[3fr,1fr,1fr] font-medium border-t border-b-2 border-gray-200 bg-gray-50 text-gray-900 drop-shadow-sm"}>
                    <div className={"flex font-bold pr-1.5 pl-3 py-3 items-center"}>
                        <RectangleStackIcon className={"w-4 h-4 mr-1.5"} />
                        <span>{t("Type")}</span>
                    </div>
                    <div className={"flex font-bold px-1.5 py-3 items-center"}>
                        <ClockIcon className={"w-4 h-4 mr-1.5"} />
                        <span>{t("Start")}</span>
                    </div>
                    <div className={"flex font-bold pl-1.5 pr-3 py-3 items-center justify-end"}>
                        <UsersIcon className={"w-4 h-4 mr-1.5"} />
                        <span>{t("Personen")}</span>
                    </div>
                </div>

                <div className={"PackagesCartItemSlotsNormal"}>
                    {filterAccessItemSlots(packageCartItemSlots).map((itemSlot, index) => {
                        if (SHOW_ITEM_SLOT_RANGE_ONLY) {
                            //hide those, that aren`t first or last
                            return (
                                <BookingRecapitulationAccessSlotItem
                                    key={index}
                                    index={index}
                                    itemSlot={itemSlot}
                                    newStyle={true}
                                    personsCount={props.packagesCart.drivers_count_adult + props.packagesCart.drivers_count_child}
                                    showPersonsCount={props.packagesCategory === PackageCategory.Event && DEFAULT_HALL !== HallCode.Zwolle}
                                />
                            );
                        }
                        return (
                            <BookingRecapitulationSlotItem
                                key={index}
                                index={index}
                                itemSlot={itemSlot}
                                newStyle={true}
                                personsCount={props.packagesCart.drivers_count_adult + props.packagesCart.drivers_count_child}
                                showPersonsCount={props.packagesCategory === PackageCategory.Event && DEFAULT_HALL !== HallCode.Zwolle}
                            />
                        );
                    })}
                </div>
                {SHOW_IMPORTANT_ARRIVE_TIME && (
                    <div className={"w-full border-t px-1"}>
                        <ImportantArriveTime selectedUpsells={props.packagesCartItemsOutstanding || []} />
                    </div>
                )}
            </div>
        </BookingCard>
    );
};

export default SoldTimeslots;
